// src/hooks/useExportExcel.js
// ============================================================
//  GFM3 — Hook utilitaire : export Excel avec branding (§3.52)
//
//  Migration SheetJS → ExcelJS.
//  SheetJS en version communautaire IGNORE les propriétés `.s`
//  (police, remplissage, bordures) : les exports sortaient bruts.
//  ExcelJS gère nativement styles, images, volets figés, filtres
//  et mise en page d'impression.
//
//  Prérequis :  npm install exceljs
//
//  L'API publique est inchangée :
//    const { exporting, exportToExcel, exportMultiSheet } = useExportExcel();
// ============================================================

import { useState, useCallback } from 'react';
import ExcelJS from 'exceljs';

/* ─── Charte graphique GFM3 ─────────────────────────────────── */
const MARINE     = 'FF0F2644';   // bleu marine institutionnel
const MARINE_CLR = 'FF1B3A63';   // variante claire
const OR         = 'FFC9A227';   // filet doré
const GRIS_BAND  = 'FFE2E8F0';   // bandeau de titre
const GRIS_LIGNE = 'FFF7F9FC';   // lignes alternées
const BORDURE    = 'FFB8C4D4';

const SOCIETE = {
  nom:     'GFM3 — Génération Femme du Troisième Millénaire',
  adresse: '27 BP 283 Abidjan 27  |  Siège social : Abidjan Angré 7ème tranche',
  legal:   'N°CNPS : 159633  |  N°Contribuable : 0913190U',
  contact: 'gfm3@yahoo.fr  |  27 22 52 20 65  |  www.gfm3.org',
};

/* Logo — chargé une seule fois puis mis en cache */
let logoCache;
async function chargerLogo() {
  if (logoCache !== undefined) return logoCache;
  try {
    const r = await fetch('/logo_gfm3.png');
    if (!r.ok) throw new Error('logo introuvable');
    logoCache = await r.arrayBuffer();
  } catch {
    logoCache = null;          // export sans logo, sans bloquer
  }
  return logoCache;
}

const bordureFine = {
  top:    { style: 'thin', color: { argb: BORDURE } },
  left:   { style: 'thin', color: { argb: BORDURE } },
  bottom: { style: 'thin', color: { argb: BORDURE } },
  right:  { style: 'thin', color: { argb: BORDURE } },
};

/**
 * Construit une feuille complète : en-tête institutionnel, titre,
 * tableau stylé, totaux de colonnes numériques, mise en page.
 */
function construireFeuille(wb, ws, { rows, columns, title, sheetName, logo }) {
  const nbCols = Math.max(columns.length, 1);
  const derCol = String.fromCharCode(64 + Math.min(nbCols, 26));

  /* ── 1. Bandeau institutionnel (lignes 1 à 5) ─────────────── */
  ws.mergeCells(`A1:${derCol}1`);
  ws.mergeCells(`A2:${derCol}2`);
  ws.mergeCells(`A3:${derCol}3`);
  ws.mergeCells(`A4:${derCol}4`);

  const ligneNom = ws.getCell('A1');
  ligneNom.value = SOCIETE.nom;
  ligneNom.font = { name: 'Calibri', size: 14, bold: true, color: { argb: 'FFFFFFFF' } };
  ligneNom.alignment = { vertical: 'middle', horizontal: 'left', indent: logo ? 9 : 1 };

  [['A2', SOCIETE.adresse], ['A3', SOCIETE.legal], ['A4', SOCIETE.contact]].forEach(([ref, txt]) => {
    const c = ws.getCell(ref);
    c.value = txt;
    c.font = { name: 'Calibri', size: 9, color: { argb: 'FFD6DEEA' } };
    c.alignment = { vertical: 'middle', horizontal: 'left', indent: logo ? 9 : 1 };
  });

  for (let r = 1; r <= 4; r++) {
    const row = ws.getRow(r);
    row.height = r === 1 ? 26 : 14;
    for (let c = 1; c <= nbCols; c++) {
      row.getCell(c).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: MARINE } };
    }
  }

  // Filet doré sous le bandeau
  const filet = ws.getRow(5);
  filet.height = 4;
  for (let c = 1; c <= nbCols; c++) {
    filet.getCell(c).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: OR } };
  }

  // Logo ancré en haut à gauche
  if (logo) {
    const imgId = wb.addImage({ buffer: logo, extension: 'png' });
    ws.addImage(imgId, {
      tl: { col: 0.15, row: 0.15 },
      ext: { width: 74, height: 74 },
      editAs: 'oneCell',
    });
  }

  /* ── 2. Titre du document (ligne 6) ───────────────────────── */
  ws.mergeCells(`A6:${derCol}6`);
  const cTitre = ws.getCell('A6');
  cTitre.value = title || sheetName;
  cTitre.font = { name: 'Calibri', size: 13, bold: true, color: { argb: MARINE } };
  cTitre.alignment = { vertical: 'middle', horizontal: 'center' };
  cTitre.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: GRIS_BAND } };
  ws.getRow(6).height = 24;

  /* ── 3. Contexte d'édition (ligne 7) ──────────────────────── */
  ws.mergeCells(`A7:${derCol}7`);
  const cMeta = ws.getCell('A7');
  cMeta.value = `Édité le ${new Date().toLocaleString('fr-FR', { dateStyle: 'long', timeStyle: 'short' })}`
              + `   •   ${rows.length} enregistrement${rows.length > 1 ? 's' : ''}`;
  cMeta.font = { name: 'Calibri', size: 9, italic: true, color: { argb: 'FF64748B' } };
  cMeta.alignment = { vertical: 'middle', horizontal: 'right' };
  ws.getRow(7).height = 16;

  ws.getRow(8).height = 6;   // respiration

  /* ── 4. En-têtes du tableau (ligne 9) ─────────────────────── */
  const LIGNE_ENTETE = 9;
  const entete = ws.getRow(LIGNE_ENTETE);
  columns.forEach((col, i) => {
    const c = entete.getCell(i + 1);
    c.value = col.label;
    c.font = { name: 'Calibri', size: 10, bold: true, color: { argb: 'FFFFFFFF' } };
    c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: MARINE_CLR } };
    c.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
    c.border = {
      top:    { style: 'thin', color: { argb: MARINE } },
      left:   { style: 'thin', color: { argb: MARINE } },
      bottom: { style: 'medium', color: { argb: OR } },
      right:  { style: 'thin', color: { argb: MARINE } },
    };
  });
  entete.height = 30;

  /* ── 5. Données ───────────────────────────────────────────── */
  rows.forEach((row, ri) => {
    const r = ws.getRow(LIGNE_ENTETE + 1 + ri);
    columns.forEach((col, ci) => {
      const brut = row[col.key] ?? '';
      const val  = col.format ? col.format(brut, row) : brut;
      const c    = r.getCell(ci + 1);

      // Un nombre reste un nombre : tri et totaux restent possibles dans Excel
      const estNombre = typeof val === 'number'
        || (col.format == null && val !== '' && !isNaN(val) && typeof brut !== 'boolean'
            && /^-?\d+([.,]\d+)?$/.test(String(val)));

      if (estNombre) {
        c.value = Number(String(val).replace(',', '.'));
        c.numFmt = '# ##0';
        c.alignment = { horizontal: 'right', vertical: 'middle' };
      } else {
        c.value = val === '' ? null : val;
        c.alignment = { horizontal: 'left', vertical: 'middle', wrapText: false };
      }

      c.font = { name: 'Calibri', size: 10, color: { argb: 'FF1E293B' } };
      c.border = bordureFine;
      if (ri % 2 === 1) {
        c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: GRIS_LIGNE } };
      }
    });
    r.height = 17;
  });

  /* ── 6. Largeurs, filtre, volets figés ────────────────────── */
  ws.columns = columns.map(c => ({ width: c.width || 18 }));

  if (rows.length) {
    ws.autoFilter = {
      from: { row: LIGNE_ENTETE, column: 1 },
      to:   { row: LIGNE_ENTETE + rows.length, column: nbCols },
    };
  }
  ws.views = [{ state: 'frozen', ySplit: LIGNE_ENTETE, xSplit: 0 }];

  /* ── 7. Mise en page d'impression ─────────────────────────── */
  ws.pageSetup = {
    orientation: nbCols > 8 ? 'landscape' : 'portrait',
    fitToPage: true,
    fitToWidth: 1,
    fitToHeight: 0,
    paperSize: 9,                       // A4
    margins: { left: 0.4, right: 0.4, top: 0.5, bottom: 0.5, header: 0.2, footer: 0.2 },
    printTitlesRow: `${LIGNE_ENTETE}:${LIGNE_ENTETE}`,
  };
  ws.headerFooter = {
    oddFooter: '&L&"Calibri,Italic"&8GFM3 — Gestion des Ressources Humaines'
             + '&C&"Calibri"&8Page &P / &N'
             + '&R&"Calibri,Italic"&8Document interne',
  };
}

export function useExportExcel() {
  const [exporting, setExporting] = useState(false);

  const telecharger = async (wb, filename) => {
    wb.creator      = 'GFM3 RH';
    wb.company      = SOCIETE.nom;
    wb.created      = new Date();
    wb.lastModifiedBy = 'GFM3 RH';

    const buffer = await wb.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  };

  const nomFeuilleSur = (brut, utilises) => {
    const base = (brut || 'Feuille').replace(/[\\/*?:[\]]/g, '').slice(0, 31) || 'Feuille';
    let nom = base, n = 2;
    while (utilises.has(nom)) { nom = `${base.slice(0, 28)}(${n})`; n++; }
    utilises.add(nom);
    return nom;
  };

  /**
   * exportToExcel({ rows, columns, filename, sheetName, title })
   */
  const exportToExcel = useCallback(async ({
    rows = [],
    columns = [],
    filename = 'GFM3_Export.xlsx',
    sheetName = 'Données',
    title = null,
  }) => {
    if (!rows.length) return;
    setExporting(true);
    try {
      const logo = await chargerLogo();
      const wb = new ExcelJS.Workbook();
      const utilises = new Set();
      const ws = wb.addWorksheet(nomFeuilleSur(sheetName, utilises), {
        properties: { defaultRowHeight: 16 },
      });
      construireFeuille(wb, ws, { rows, columns, title, sheetName, logo });
      await telecharger(wb, filename);
    } finally {
      setExporting(false);
    }
  }, []);

  /**
   * exportMultiSheet({ sheets: [{ rows, columns, sheetName, title }], filename })
   */
  const exportMultiSheet = useCallback(async ({ sheets = [], filename = 'GFM3_Export.xlsx' }) => {
    const avecDonnees = sheets.filter(sh => (sh.rows || []).length > 0);
    if (!avecDonnees.length) return;
    setExporting(true);
    try {
      const logo = await chargerLogo();
      const wb = new ExcelJS.Workbook();
      const utilises = new Set();

      avecDonnees.forEach(({ rows = [], columns = [], sheetName = 'Feuille', title = null }) => {
        const ws = wb.addWorksheet(nomFeuilleSur(sheetName, utilises), {
          properties: { defaultRowHeight: 16 },
        });
        construireFeuille(wb, ws, { rows, columns, title, sheetName, logo });
      });

      await telecharger(wb, filename);
    } finally {
      setExporting(false);
    }
  }, []);

  return { exporting, exportToExcel, exportMultiSheet };
}

/* ─── Helpers de formatage (inchangés) ─────────────────────── */

export const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString('fr-FR') : '';

export const fmtDateTime = (d) =>
  d ? new Date(d).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) : '';

export const fmtBool = (v) => v ? 'Oui' : 'Non';

export const fmtMontant = (v) =>
  v != null && v !== '' ? Number(v).toLocaleString('fr-FR') + ' XOF' : '';
